import { useCallback, useRef, useState } from 'react';
import type { IInsightMessage } from '@skystone/shared';
import { PageContainer } from '@/components/layout';
import { PageHeader, Button } from '@/components/ui';
import { InsightChat, type InsightChatHandle } from '@/components/insight/InsightChat';
import { insightApi } from '@/services/insight-api';
import { formatCostUsd } from '@/lib/format-cost';

const EXAMPLE_PROMPTS: readonly string[] = [
  'Which team has the highest cycle time P85?',
  'Compare deploy frequency across products this quarter.',
  'Show me the products with the lowest test coverage.',
  'What does CFR mean?',
  'How has throughput changed in the last 4 weeks?',
  'Which products had the most incidents last month?',
  'What is the difference between Lead Time and Cycle Time P85?',
  'Which epics have the lowest completion rate?',
];

export function AskPage() {
  const chatRef = useRef<InsightChatHandle>(null);
  const [hasHistory, setHasHistory] = useState(false);
  const [sessionCostUsd, setSessionCostUsd] = useState<number | null>(null);

  const handleSend = useCallback(async (messages: IInsightMessage[]) => {
    return insightApi.chat({ messages });
  }, []);

  const handleClear = useCallback(() => {
    chatRef.current?.clear();
  }, []);

  const sessionCostText = formatCostUsd(sessionCostUsd);
  const action = hasHistory ? (
    <div className="flex items-center gap-3">
      {sessionCostText && (
        <span className="text-xs text-muted-foreground" aria-label="Session cost so far">
          Session: {sessionCostText}
        </span>
      )}
      <Button variant="outline" size="sm" onClick={handleClear} aria-label="New chat">
        New chat
      </Button>
    </div>
  ) : undefined;

  return (
    <PageContainer variant="narrow" className="space-y-6 p-6">
      <PageHeader
        title="Ask AI"
        subtitle="Ask anything about your engineering data — DevEx, DORA, code quality, work tracking. Read-only."
        action={action}
      />

      <InsightChat
        ref={chatRef}
        onSend={handleSend}
        emptyStatePrompts={EXAMPLE_PROMPTS}
        placeholder="Ask a question about your engineering data…"
        inputAriaLabel="Ask a question"
        messageListClassName="min-h-[55vh] max-h-[70vh] sm:min-h-[60vh] lg:max-h-[75vh]"
        onHistoryChange={setHasHistory}
        onSessionCostChange={setSessionCostUsd}
      />
    </PageContainer>
  );
}
